import { FC, useEffect, useRef, useState } from "react";
import { Button, Modal } from "@monolith-forensics/monolith-ui";

import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import type { LabAsset } from "../labAssets.types";
import { getLabAssetDisplayName } from "../labAssets.utils";
import EditLabAssetForm from "../Forms/EditLabAssetForm";

// Matches CreateLabAssetModal so the two forms render at the same size.
const MODAL_WIDTH = 550;

interface EditLabAssetModalProps {
  show: boolean;
  onClose: () => void;
  // Fires after a successful save, before the modal closes. The mutation hook
  // already invalidates the item and list caches, so most hosts need nothing.
  onSubmit?: () => void;
  defaultInfo: LabAsset;
}

/**
 * Edit modal for the shared asset fields (ENG-1670). Wraps EditLabAssetForm
 * and owns the close guard: Cancel on a dirty form swaps the content for an
 * in-place "Discard changes?" panel rather than closing. The form stays
 * mounted (hidden) behind the panel so "Keep editing" returns to the edits
 * intact; "Discard" closes, and the Modal unmounts the form on close so the
 * next open re-seeds from the current record.
 *
 * This is the first unsaved-changes guard in the app; keep the shape simple
 * so Storage/Evidence edits can lift it later.
 */
export const EditLabAssetModal: FC<EditLabAssetModalProps> = ({
  show,
  onClose,
  onSubmit,
  defaultInfo,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const formRef = useRef<HTMLDivElement | null>(null);
  const keepEditingRef = useRef<HTMLButtonElement | null>(null);

  // Swapping the content hides the element that had focus, which would drop
  // focus to <body> inside the modal's focus trap. Re-home it.
  useEffect(() => {
    if (!show) return;
    if (confirmDiscard) {
      keepEditingRef.current?.focus();
    } else {
      formRef.current?.querySelector<HTMLElement>("input, textarea")?.focus();
    }
  }, [confirmDiscard, show]);

  const closeAndReset = () => {
    setConfirmDiscard(false);
    setDirty(false);
    onClose();
  };

  // User-initiated close (the form's Cancel, or the discard panel's Discard;
  // the mfui Modal has no overlay/Esc dismiss and its close button isn't
  // enabled here). Guarded because closing mid-request would still save.
  const handleClose = () => {
    if (submitting) return;
    if (dirty && !confirmDiscard) {
      setConfirmDiscard(true);
      return;
    }
    closeAndReset();
  };

  // Success path. Deliberately unguarded: the form's submitting flag is still
  // true here (it clears in the form's finally), so routing through
  // handleClose would only work by reading a stale closure.
  const handleSubmit = () => {
    onSubmit?.();
    closeAndReset();
  };

  return (
    <Modal open={show} onClose={handleClose}>
      {confirmDiscard ? (
        <div style={{ width: MODAL_WIDTH }}>
          <Modal.Title>Discard changes?</Modal.Title>
          <div style={{ fontWeight: 500, margin: "20px 0px" }}>
            You have unsaved changes to this asset.
          </div>
          {/* Keep editing is the contained primary (and takes focus) so the
              default action is the one that preserves the work; Discard is
              the subtle button in the cancel slot, matching the form's own
              Cancel / Save layout. */}
          <ButtonMenu>
            <Button size="xs" variant="subtle" onClick={handleClose}>
              Discard
            </Button>
            <Button
              size="xs"
              color="primary"
              variant="contained"
              ref={keepEditingRef}
              onClick={() => setConfirmDiscard(false)}
            >
              Keep editing
            </Button>
          </ButtonMenu>
        </div>
      ) : (
        <>
          <Modal.Title>Edit Asset</Modal.Title>
          <Modal.SubTitle style={{ marginBottom: 15 }}>
            {getLabAssetDisplayName(defaultInfo) || "Unnamed Asset"}
          </Modal.SubTitle>
        </>
      )}

      {/* Kept mounted (hidden) behind the discard panel so "Keep editing"
          returns to the user's edits. Keyed by uuid so a host that swaps the
          record while open starts a fresh form. */}
      <div ref={formRef} style={{ display: confirmDiscard ? "none" : "block" }}>
        <EditLabAssetForm
          key={defaultInfo.uuid}
          width={MODAL_WIDTH}
          asset={defaultInfo}
          onSubmit={handleSubmit}
          onCancel={handleClose}
          onSubmittingChange={setSubmitting}
          onDirtyChange={setDirty}
        />
      </div>
    </Modal>
  );
};
